import React, { useState } from "react";
import {
  Button,
  Card,
  Col,
  Collapse,
  Divider,
  Form,
  InputNumber,
  Row,
  Select,
  Typography,
} from "antd";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import { useMediaQuery } from "react-responsive";

const { Title, Text } = Typography;

/* ---------------- PRICING ---------------- */
const gbpPricing = [
  { wc: 500, pass: 20, merit: 25, distinction: 31 },
  { wc: 1000, pass: 35, merit: 44, distinction: 55 },
  { wc: 1500, pass: 50, merit: 63, distinction: 78 },
  { wc: 2000, pass: 63, merit: 79, distinction: 98 },
  { wc: 2500, pass: 78, merit: 99, distinction: 148 },
  { wc: 3000, pass: 95, merit: 119, distinction: 148 },
  { wc: 4000, pass: 110, merit: 138, distinction: 172 },
  { wc: 5000, pass: 115, merit: 144, distinction: 180 },
  { wc: 6000, pass: 125, merit: 156, distinction: 195 },
  { wc: 6500, pass: 136, merit: 170, distinction: 213 },
  { wc: 7000, pass: 146, merit: 183, distinction: 228 },
  { wc: 7500, pass: 156, merit: 195, distinction: 244 },
  { wc: 8000, pass: 166, merit: 208, distinction: 260 },
  { wc: 9000, pass: 186, merit: 233, distinction: 290 },
  { wc: 10000, pass: 205, merit: 256, distinction: 320 },
  { wc: 11000, pass: 220, merit: 275, distinction: 344 },
  { wc: 12000, pass: 240, merit: 300, distinction: 375 },
  { wc: 13000, pass: 260, merit: 325, distinction: 406 },
  { wc: 14000, pass: 280, merit: 350, distinction: 438 },
  { wc: 15000, pass: 300, merit: 375, distinction: 469 },
  { wc: 16000, pass: 320, merit: 400, distinction: 500 },
  { wc: 17000, pass: 340, merit: 425, distinction: 531 },
  { wc: 18000, pass: 360, merit: 450, distinction: 563 },
  { wc: 19000, pass: 380, merit: 475, distinction: 594 },
  { wc: 20000, pass: 400, merit: 500, distinction: 625 },
];

const audPricing = [
  { wc: 500, pass: 25, merit: 35 },
  { wc: 1000, pass: 50, merit: 60 },
  { wc: 1500, pass: 75, merit: 85 },
  { wc: 2000, pass: 100, merit: 120 },
  { wc: 2500, pass: 125, merit: 150 },
  { wc: 3000, pass: 150, merit: 200 },
];

const getDiscountPercent = (n) => {
  if (n >= 4) return 15;
  if (n >= 3) return 10;
  if (n >= 2) return 5;
  return 0;
};

const findPricingBand = (rows, wc, currency) => {
  if (!wc) return null;

  // Special rule: AUD pricing for 5000+ words uses a flat 0.039 per-word rate.
  if (currency === "AUD" && wc >= 5000) {
    const price = Math.round(wc * 0.039);
    return { wc, pass: price, merit: price };
  }

  const band = rows.find((r) => r.wc >= wc);
  if (band) return band;

  const last = rows.at(-1);
  if (!last) return null;
  const extrapolate = (value) =>
    value != null ? Math.round((value / last.wc) * wc) : undefined;
  return {
    wc,
    pass: extrapolate(last.pass),
    merit: extrapolate(last.merit),
    distinction: extrapolate(last.distinction),
  };
};

/* ---------------- REQUIREMENT ---------------- */
const calculateRequirementWC = (type, value, extra, analysisTool) => {
  const rawUnits = Number(value);
  const n = Number.isFinite(rawUnits) ? rawUnits : 1;
  const writingWC = Number.isFinite(rawUnits) ? rawUnits : 0;

  switch (type) {
    case "report":
      return n * 300;
    case "presentation":
      return n * 100;
    case "presentation_with_notes":
      return n * 150;
    case "poster":
      return n * 1000;
    case "research":
      return 1500;
    case "ethics":
      return 300;
    case "accounts":
      return writingWC + 1000;
    case "maths":
      return writingWC + 1500;
    case "analysis":
      if (analysisTool === "excel") return 500;
      if (analysisTool === "spss") return 1000;
      if (analysisTool === "other") return 1500;
      return 500;
    case "programming":
      return writingWC + 1500;
    case "website":
      return 5000;
    case "teaching":
      return writingWC + 1000;
    default:
      return null;
  }
};

const QuotationPricing = () => {
  const [form] = Form.useForm();
  const isMobile = useMediaQuery({ query: "(max-width: 768px)" });

  const [savedBriefs, setSavedBriefs] = useState([]);
  const [editingBriefIndex, setEditingBriefIndex] = useState(null);
  const [editingBriefOriginal, setEditingBriefOriginal] = useState(null);

  const handleNewBrief = () => {
    const currentWorks = form.getFieldValue("worksList") || [];
    const currentRequirements = form.getFieldValue("requirementsList") || [];

    setSavedBriefs((prev) => [
      ...prev,
      {
        worksList: currentWorks,
        requirementsList: currentRequirements,
        worksBase: totalBase,
        requirementsBase: totalRequirementBase,
      },
    ]);

    form.setFieldsValue({ worksList: [{}], requirementsList: [{}] });
  };

  const handleEditBrief = (index) => {
    const brief = savedBriefs[index];
    if (!brief) return;
    setEditingBriefIndex(index);
    setEditingBriefOriginal(brief);
    setSavedBriefs((prev) => prev.filter((_, i) => i !== index));
    form.setFieldsValue({
      worksList: brief?.worksList?.length ? brief.worksList : [{}],
      requirementsList: brief?.requirementsList?.length
        ? brief.requirementsList
        : [{}],
    });
  };

  const handleCancelEditBrief = () => {
    if (editingBriefIndex === null || !editingBriefOriginal) return;
    setSavedBriefs((prev) => {
      const next = [...prev];
      next.splice(editingBriefIndex, 0, editingBriefOriginal);
      return next;
    });
    setEditingBriefIndex(null);
    setEditingBriefOriginal(null);
    form.setFieldsValue({ worksList: [{}], requirementsList: [{}] });
  };

  const handleRemoveBrief = (index) => {
    setSavedBriefs((prev) => prev.filter((_, i) => i !== index));
  };

  const mobileItemLayout = isMobile
    ? {
        labelCol: { span: 24 },
        wrapperCol: { span: 24 },
      }
    : undefined;

  const worksList = Form.useWatch("worksList", form) || [];
  const requirementsList = Form.useWatch("requirementsList", form) || [];

  const briefCount = savedBriefs.length + 1;

  const worksCurrencySet = new Set(
    worksList.map((w) => w?.currency).filter(Boolean),
  );
  const worksCurrencyLabel =
    worksCurrencySet.size === 1 ? [...worksCurrencySet][0] : "";

  const requirementsCurrencySet = new Set(
    requirementsList.map((r) => r?.currency).filter(Boolean),
  );
  const requirementsCurrencyLabel =
    requirementsCurrencySet.size === 1 ? [...requirementsCurrencySet][0] : "";

  const requirementResults = requirementsList.map((req) => {
    const wc = calculateRequirementWC(
      req?.type,
      req?.value,
      req?.extraWC,
      req?.analysisTool,
    );
    if (!wc || !req?.currency) {
      return { wc, base: 0, final: 0 };
    }
    const rows = req.currency === "AUD" ? audPricing : gbpPricing;
    const band = findPricingBand(rows, wc, req.currency);
    if (!band) return { wc, base: 0, final: 0 };

    let price = band.pass;

    if (req?.type === "website" && req.currency === "GBP") {
      price = 144;
    }

    if (req?.type === "teaching" && req.currency === "GBP") {
      if (wc >= 4000 && wc <= 6000) {
        price = 115;
      }
    }

    return {
      wc,
      base: price,
      final: price,
    };
  });

  const totalRequirementBase = requirementResults.reduce(
    (sum, item) => sum + (item?.base || 0),
    0,
  );

  const totalRequirementFinal = requirementResults.reduce(
    (sum, item) => sum + (item?.final || 0),
    0,
  );

  /* -------- SAFE PRICE CALC -------- */
  const workPrices = worksList.map((work) => {
    if (!work?.wc || !work?.currency || !work?.level) {
      return { base: 0, final: 0 };
    }

    const rows = work.currency === "AUD" ? audPricing : gbpPricing;

    const band = findPricingBand(rows, work.wc, work.currency);
    if (!band) return { base: 0, final: 0 };

    let price = 0;

    if (work.level === "pass") price = band.pass;
    else if (work.level === "merit") price = band.merit ?? band.pass * 1.2;
    else if (work.level === "distinction") {
      if (work.currency === "AUD") return { base: 0, final: 0 };
      price = band.distinction ?? band.pass * 1.4;
    }

    return {
      base: price,
      final: price,
    };
  });

  const totalBase = workPrices.reduce(
    (sum, item) => sum + (item?.base || 0),
    0,
  );

  const totalFinal = workPrices.reduce(
    (sum, item) => sum + (item?.final || 0),
    0,
  );

  const handleSaveEditBrief = () => {
    if (editingBriefIndex === null) return;
    const currentWorks = form.getFieldValue("worksList") || [];
    const currentRequirements = form.getFieldValue("requirementsList") || [];

    const updated = {
      worksList: currentWorks,
      requirementsList: currentRequirements,
      worksBase: totalBase,
      requirementsBase: totalRequirementBase,
    };

    setSavedBriefs((prev) => {
      const next = [...prev];
      next.splice(editingBriefIndex, 0, updated);
      return next;
    });
    setEditingBriefIndex(null);
    setEditingBriefOriginal(null);
    form.setFieldsValue({ worksList: [{}], requirementsList: [{}] });
  };

  const currentBriefBaseTotal = totalBase + totalRequirementBase;
  const savedBriefsWithAmountCount = savedBriefs.filter((b) => {
    const baseTotal = (b?.worksBase || 0) + (b?.requirementsBase || 0);
    return baseTotal > 0;
  }).length;
  const effectiveBriefCount =
    savedBriefsWithAmountCount + (currentBriefBaseTotal > 0 ? 1 : 0);

  const overallDiscount = getDiscountPercent(effectiveBriefCount);

  const overallWorksBase =
    savedBriefs.reduce((s, b) => s + (b?.worksBase || 0), 0) + totalBase;
  const overallRequirementsBase =
    savedBriefs.reduce((s, b) => s + (b?.requirementsBase || 0), 0) +
    totalRequirementBase;

  const overallBase = overallWorksBase + overallRequirementsBase;
  const overallFinal = overallBase * (1 - overallDiscount / 100);

  return (
    <div style={{ padding: 24, textAlign: "left" }}>
      {/* <Title level={3} style={{ textAlign: "left" }}>
        Quotation Pricing
      </Title> */}

      {savedBriefs.length > 0 && (
        <Card title="Saved Briefs" style={{ marginBottom: 16 }}>
          <Collapse
            accordion
            items={savedBriefs.map((b, idx) => {
              const worksCount = (b?.worksList || []).length;
              const reqCount = (b?.requirementsList || []).length;
              const baseTotal =
                (b?.worksBase || 0) + (b?.requirementsBase || 0);

              return {
                key: String(idx),
                label: `Brief ${idx + 1} - Total ${baseTotal.toFixed(2)}`,
                children: (
                  <div>
                    <Row justify="space-between" style={{ marginBottom: 8 }}>
                      <Text>Works: {worksCount}</Text>
                      <Text>Requirements: {reqCount}</Text>
                      <div>
                        <Button
                          onClick={() => handleEditBrief(idx)}
                          style={{ marginRight: 8 }}
                        >
                          Edit
                        </Button>
                        <Button danger onClick={() => handleRemoveBrief(idx)}>
                          Remove Brief
                        </Button>
                      </div>
                    </Row>
                    <Row justify="space-between">
                      <Text>
                        Works Total: {Number(b?.worksBase || 0).toFixed(2)}
                      </Text>
                      <Text>
                        Requirements Total:{" "}
                        {Number(b?.requirementsBase || 0).toFixed(2)}
                      </Text>
                      <Text strong>Total: {baseTotal.toFixed(2)}</Text>
                    </Row>
                  </div>
                ),
              };
            })}
          />
        </Card>
      )}

      {/* WORK TABLE */}
      <Card>
        <Form
          form={form}
          layout={isMobile ? "vertical" : "horizontal"}
          initialValues={{ worksList: [{}] }}
        >
          <Form.List name="worksList">
            {(fields, { add, remove }) => (
              <>
                <Row
                  justify={isMobile ? "start" : "end"}
                  style={{ marginBottom: 12 }}
                >
                  {editingBriefIndex !== null ? (
                    <>
                      <Button
                        type="primary"
                        onClick={handleSaveEditBrief}
                        style={{ marginRight: 8 }}
                      >
                        Save Changes
                      </Button>
                      <Button
                        onClick={handleCancelEditBrief}
                        style={{ marginRight: 8 }}
                      >
                        Cancel
                      </Button>
                    </>
                  ) : (
                    <Button onClick={handleNewBrief} style={{ marginRight: 8 }}>
                      New Brief
                    </Button>
                  )}
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => add()}
                  >
                    Add Work
                  </Button>
                </Row>

                {/* HEADER */}
                {!isMobile && (
                  <Row
                    gutter={isMobile ? [0, 10] : [10, 10]}
                    style={{ marginBottom: 8, textAlign: "left" }}
                  >
                    <Col xs={24} sm={4}>
                      <Text type="secondary">Currency</Text>
                    </Col>
                    <Col xs={24} sm={4}>
                      <Text type="secondary">Word Count</Text>
                    </Col>
                    <Col xs={24} sm={4}>
                      <Text type="secondary">Level</Text>
                    </Col>
                    <Col xs={24} sm={4}>
                      <Text type="secondary">Base</Text>
                    </Col>
                    <Col xs={24} sm={4}>
                      <Text type="secondary">Final</Text>
                    </Col>
                    <Col xs={24} sm={4} style={{ textAlign: "right" }}>
                      <Text type="secondary">Action</Text>
                    </Col>
                  </Row>
                )}

                {fields.map(({ key, name, ...restField }, i) => {
                  const price = workPrices[i] || { base: 0, final: 0 };
                  const currency = worksList[i]?.currency || "";
                  // ✅ MOBILE VIEW
                  if (isMobile) {
                    return (
                      <div
                        key={key}
                        style={{
                          padding: 12,
                          border: "1px solid #f0f0f0",
                          borderRadius: 8,
                          marginBottom: 10,
                        }}
                      >
                        <Form.Item
                          {...restField}
                          name={[name, "currency"]}
                          label="Currency"
                          style={{ width: "100%" }}
                        >
                          <Select style={{ width: "100%" }}>
                            <Select.Option value="GBP">GBP</Select.Option>
                            <Select.Option value="AUD">AUD</Select.Option>
                          </Select>
                        </Form.Item>

                        <Form.Item
                          {...restField}
                          name={[name, "wc"]}
                          label="Word Count"
                          style={{ width: "100%" }}
                        >
                          <InputNumber style={{ width: "100%" }} />
                        </Form.Item>

                        <Form.Item
                          {...restField}
                          name={[name, "level"]}
                          label="Level"
                          style={{ width: "100%" }}
                        >
                          <Select style={{ width: "100%" }}>
                            <Select.Option value="pass">Pass</Select.Option>
                            <Select.Option value="merit">Merit</Select.Option>
                            <Select.Option value="distinction">
                              Distinction
                            </Select.Option>
                          </Select>
                        </Form.Item>

                        <Text>
                          Base: {price.base ? `${currency} ${price.base}` : "-"}
                        </Text>
                        <br />
                        <Text>
                          Final:{" "}
                          {price.final
                            ? `${currency} ${price.final.toFixed(2)}`
                            : "-"}
                        </Text>

                        {fields.length > 1 && (
                          <Button
                            danger
                            block
                            icon={<DeleteOutlined />}
                            onClick={() => remove(name)}
                            style={{ marginTop: 10 }}
                          >
                            Remove
                          </Button>
                        )}
                      </div>
                    );
                  }

                  return (
                    <Row
                      key={key}
                      gutter={isMobile ? [0, 10] : [10, 10]}
                      align="middle"
                      style={{
                        padding: 12,
                        border: "1px solid #f0f0f0",
                        borderRadius: 8,
                        marginBottom: 10,
                        textAlign: "left",
                      }}
                    >
                      <Col xs={24} sm={4}>
                        <Form.Item
                          {...restField}
                          name={[name, "currency"]}
                          label={isMobile ? "Currency" : undefined}
                          style={{ marginBottom: 0 }}
                          {...(mobileItemLayout || {})}
                        >
                          <Select
                            placeholder="Currency"
                            style={{ width: "100%" }}
                          >
                            <Select.Option value="GBP">GBP</Select.Option>
                            <Select.Option value="AUD">AUD</Select.Option>
                          </Select>
                        </Form.Item>
                      </Col>

                      <Col xs={24} sm={4}>
                        <Form.Item
                          {...restField}
                          name={[name, "wc"]}
                          label={isMobile ? "Word Count" : undefined}
                          style={{ marginBottom: 0, width: "100%" }}
                          {...(mobileItemLayout || {})}
                        >
                          <InputNumber
                            style={{ width: "100%" }}
                            placeholder="WC"
                          />
                        </Form.Item>
                      </Col>

                      <Col xs={24} sm={4}>
                        <Form.Item
                          {...restField}
                          name={[name, "level"]}
                          label={isMobile ? "Level" : undefined}
                          style={{ marginBottom: 0, width: "100%" }}
                          {...(mobileItemLayout || {})}
                        >
                          <Select placeholder="Level" style={{ width: "100%" }}>
                            <Select.Option value="pass">Pass</Select.Option>
                            <Select.Option value="merit">Merit</Select.Option>
                            <Select.Option value="distinction">
                              Distinction
                            </Select.Option>
                          </Select>
                        </Form.Item>
                      </Col>

                      <Col xs={24} sm={4}>
                        {isMobile ? (
                          <Text type="secondary" style={{ display: "block" }}>
                            Base
                          </Text>
                        ) : null}
                        <Text style={{ display: "block", textAlign: "left" }}>
                          {price.base ? `${currency} ${price.base}` : "-"}
                        </Text>
                      </Col>

                      <Col xs={24} sm={4}>
                        {isMobile ? (
                          <Text type="secondary" style={{ display: "block" }}>
                            Final
                          </Text>
                        ) : null}
                        <Text style={{ display: "block", textAlign: "left" }}>
                          {price.final
                            ? `${currency} ${price.final.toFixed(2)}`
                            : "-"}
                        </Text>
                      </Col>

                      <Col xs={24} sm={4} style={{ textAlign: "right" }}>
                        {fields.length > 1 && (
                          <Button
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() => remove(name)}
                          />
                        )}
                      </Col>
                    </Row>
                  );
                })}
              </>
            )}
          </Form.List>
        </Form>

        <Divider />

        {/* TOTAL */}
        <Row justify="space-between">
          <Text>
            Total: {worksCurrencyLabel ? `${worksCurrencyLabel} ` : ""}
            {totalBase.toFixed(2)}
          </Text>
          <Text />
          <Title level={4} style={{ textAlign: "left" }}>
            Final: {worksCurrencyLabel ? `${worksCurrencyLabel} ` : ""}
            {totalFinal.toFixed(2)}
          </Title>
        </Row>
      </Card>
      <Card
        title="Requirement Based Calculator"
        headStyle={{ textAlign: "left" }}
      >
        <Form
          form={form}
          layout={isMobile ? "vertical" : "horizontal"}
          initialValues={{ requirementsList: [{}] }}
        >
          <Form.List name="requirementsList">
            {(fields, { add, remove }) => (
              <>
                <Row
                  justify={isMobile ? "start" : "end"}
                  style={{ marginBottom: 12 }}
                >
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => add()}
                  >
                    Add Requirement
                  </Button>
                </Row>

                {!isMobile && (
                  <Row
                    gutter={[10, 10]}
                    style={{ marginBottom: 8, textAlign: "left" }}
                  >
                    <Col xs={24} md={6}>
                      <Text type="secondary">Requirement</Text>
                    </Col>
                    <Col xs={24} md={4}>
                      <Text type="secondary">Value</Text>
                    </Col>
                    <Col xs={24} md={3}>
                      <Text type="secondary">Tool</Text>
                    </Col>
                    <Col xs={24} md={5}>
                      <Text type="secondary">Currency</Text>
                    </Col>
                    <Col xs={24} md={2}>
                      <Text type="secondary">WC</Text>
                    </Col>
                    <Col xs={24} md={2}>
                      <Text type="secondary">Final</Text>
                    </Col>
                    <Col xs={24} md={2} style={{ textAlign: "right" }} />
                  </Row>
                )}

                {fields.map(({ key, name, ...restField }, i) => {
                  const result = requirementResults[i] || {
                    wc: null,
                    base: 0,
                    final: 0,
                  };
                  const reqType = requirementsList[i]?.type;
                  const currency = requirementsList[i]?.currency || "";
                  const showAnalysisTool = reqType === "analysis";
                  const showValueInput = [
                    "report",
                    "presentation",
                    "presentation_with_notes",
                    "poster",
                    "accounts",
                    "maths",
                    "programming",
                    "teaching",
                  ].includes(reqType);
                  const valueLabel = [
                    "accounts",
                    "maths",
                    "programming",
                    "teaching",
                  ].includes(reqType)
                    ? "Writing WC"
                    : reqType === "report"
                      ? "Pages"
                      : reqType === "presentation"
                        ? "Minutes/Slides/Pages"
                        : reqType === "presentation_with_notes"
                          ? "Slides"
                          : reqType === "poster"
                            ? "Posters"
                            : "Value";
                  if (isMobile) {
                    return (
                      <div
                        key={key}
                        style={{
                          padding: 12,
                          border: "1px solid #f0f0f0",
                          borderRadius: 8,
                          marginBottom: 10,
                        }}
                      >
                        <Form.Item
                          {...restField}
                          name={[name, "type"]}
                          label="Requirement"
                          style={{ width: "100%" }}
                        >
                          <Select style={{ width: "100%" }} placeholder="Type">
                            <Select.Option value="report">Report</Select.Option>
                            <Select.Option value="presentation">
                              Presentation
                            </Select.Option>
                            <Select.Option value="presentation_with_notes">
                              Presentation + Notes
                            </Select.Option>
                            <Select.Option value="poster">Poster</Select.Option>
                            <Select.Option value="research">
                              Research Proposal
                            </Select.Option>
                            <Select.Option value="ethics">Ethics</Select.Option>
                            <Select.Option value="accounts">
                              Accounts
                            </Select.Option>
                            <Select.Option value="maths">Maths</Select.Option>
                            <Select.Option value="analysis">
                              Analysis
                            </Select.Option>
                            <Select.Option value="programming">
                              Programming (Html, CSS, WIX, Canva)
                            </Select.Option>
                            <Select.Option value="website">
                              Website
                            </Select.Option>
                            <Select.Option value="teaching">
                              Teaching
                            </Select.Option>
                          </Select>
                        </Form.Item>

                        {showValueInput && (
                          <Form.Item
                            {...restField}
                            name={[name, "value"]}
                            label={valueLabel}
                            style={{ width: "100%" }}
                          >
                            <InputNumber
                              style={{ width: "100%" }}
                              placeholder={valueLabel}
                            />
                          </Form.Item>
                        )}

                        {showAnalysisTool && (
                          <Form.Item
                            {...restField}
                            name={[name, "analysisTool"]}
                            label="Tool"
                            style={{ width: "100%" }}
                          >
                            <Select
                              style={{ width: "100%" }}
                              placeholder="Tool"
                            >
                              <Select.Option value="excel">Excel</Select.Option>
                              <Select.Option value="spss">SPSS</Select.Option>
                              <Select.Option value="other">Other</Select.Option>
                            </Select>
                          </Form.Item>
                        )}

                        <Form.Item
                          {...restField}
                          name={[name, "currency"]}
                          label="Currency"
                          style={{ width: "100%" }}
                        >
                          <Select
                            style={{ width: "100%" }}
                            placeholder="Currency"
                          >
                            <Select.Option value="GBP">GBP</Select.Option>
                            <Select.Option value="AUD">AUD</Select.Option>
                          </Select>
                        </Form.Item>

                        <Text strong>WC: {result.wc || "-"}</Text>
                        <br />

                        <Text>
                          Final:{" "}
                          {result.final
                            ? `${currency} ${result.final.toFixed(2)}`
                            : "-"}
                        </Text>

                        {fields.length > 1 && (
                          <Button
                            danger
                            block
                            icon={<DeleteOutlined />}
                            onClick={() => remove(name)}
                            style={{ marginTop: 10 }}
                          >
                            Remove
                          </Button>
                        )}
                      </div>
                    );
                  }

                  return (
                    <Row
                      key={key}
                      gutter={[10, 10]}
                      align="middle"
                      style={{
                        padding: 12,
                        border: "1px solid #f0f0f0",
                        borderRadius: 8,
                        marginBottom: 10,
                        textAlign: "left",
                      }}
                    >
                      <Col xs={24} md={6}>
                        <Form.Item
                          {...restField}
                          name={[name, "type"]}
                          label={isMobile ? "Requirement" : undefined}
                          style={{ marginBottom: 0, width: "100%" }}
                          {...(mobileItemLayout || {})}
                        >
                          <Select placeholder="Type" style={{ width: "100%" }}>
                            <Select.Option value="report">Report</Select.Option>
                            <Select.Option value="presentation">
                              Presentation
                            </Select.Option>
                            <Select.Option value="presentation_with_notes">
                              Presentation + Notes
                            </Select.Option>
                            <Select.Option value="poster">Poster</Select.Option>
                            <Select.Option value="research">
                              Research
                            </Select.Option>
                            <Select.Option value="ethics">Ethics</Select.Option>
                            <Select.Option value="accounts">
                              Accounts
                            </Select.Option>
                            <Select.Option value="maths">Maths</Select.Option>
                            <Select.Option value="analysis">
                              Analysis
                            </Select.Option>
                            <Select.Option value="programming">
                              Programming (Html, CSS, WIX, Canva)
                            </Select.Option>
                            <Select.Option value="website">
                              Website
                            </Select.Option>
                            <Select.Option value="teaching">
                              Teaching
                            </Select.Option>
                          </Select>
                        </Form.Item>
                      </Col>

                      <Col xs={24} md={4}>
                        {showValueInput ? (
                          <Form.Item
                            {...restField}
                            name={[name, "value"]}
                            label={isMobile ? valueLabel : undefined}
                            style={{ marginBottom: 0, width: "100%" }}
                            {...(mobileItemLayout || {})}
                          >
                            <InputNumber
                              style={{ width: "100%" }}
                              placeholder={valueLabel}
                            />
                          </Form.Item>
                        ) : (
                          <Text type="secondary" style={{ display: "block" }}>
                            -
                          </Text>
                        )}
                      </Col>

                      <Col xs={24} md={3}>
                        {showAnalysisTool ? (
                          <Form.Item
                            {...restField}
                            name={[name, "analysisTool"]}
                            label={isMobile ? "Tool" : undefined}
                            style={{ marginBottom: 0, width: "100%" }}
                            {...(mobileItemLayout || {})}
                          >
                            <Select
                              placeholder="Tool"
                              style={{ width: "100%" }}
                            >
                              <Select.Option value="excel">Excel</Select.Option>
                              <Select.Option value="spss">SPSS</Select.Option>
                              <Select.Option value="other">Other</Select.Option>
                            </Select>
                          </Form.Item>
                        ) : (
                          <Text type="secondary" style={{ display: "block" }}>
                            -
                          </Text>
                        )}
                      </Col>

                      <Col xs={24} md={5}>
                        <Form.Item
                          {...restField}
                          name={[name, "currency"]}
                          label={isMobile ? "Currency" : undefined}
                          style={{ marginBottom: 0, width: "100%" }}
                          {...(mobileItemLayout || {})}
                        >
                          <Select
                            placeholder="Currency"
                            style={{ width: isMobile ? "100%" : "100%" }}
                          >
                            <Select.Option value="GBP">GBP</Select.Option>
                            <Select.Option value="AUD">AUD</Select.Option>
                          </Select>
                        </Form.Item>
                      </Col>

                      <Col xs={24} md={2}>
                        {isMobile ? (
                          <Text type="secondary" style={{ display: "block" }}>
                            WC
                          </Text>
                        ) : null}
                        <Text strong>{result.wc || "-"}</Text>
                      </Col>

                      <Col xs={24} md={2}>
                        {isMobile ? (
                          <Text type="secondary" style={{ display: "block" }}>
                            Final
                          </Text>
                        ) : null}
                        <Text>
                          {result.final
                            ? `${currency} ${result.final.toFixed(2)}`
                            : "-"}
                        </Text>
                      </Col>

                      <Col xs={24} md={2} style={{ textAlign: "right" }}>
                        {fields.length > 1 && (
                          <Button
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() => remove(name)}
                          />
                        )}
                      </Col>
                    </Row>
                  );
                })}
              </>
            )}
          </Form.List>
        </Form>

        <Divider />

        <Row justify="space-between">
          <Text />
          <Title level={4} style={{ textAlign: "left" }}>
            Final Total:{" "}
            {requirementsCurrencyLabel ? `${requirementsCurrencyLabel} ` : ""}
            {totalRequirementFinal.toFixed(2)}
          </Title>
        </Row>
      </Card>

      {effectiveBriefCount > 1 && (
        <div
          style={{
            position: "sticky",
            bottom: 0,
            background: "#fff",
            borderTop: "1px solid #f0f0f0",
            padding: "12px 16px",
            marginTop: 12,
            zIndex: 10,
          }}
        >
          <Row justify="space-between" align="middle">
            <Text strong>Total Briefs: {effectiveBriefCount}</Text>
            <Text strong>Total: {overallBase.toFixed(2)}</Text>
            <Text>Discount: {overallDiscount}%</Text>
            <Text strong>Final: {overallFinal.toFixed(2)}</Text>
          </Row>
        </div>
      )}

      {/* <Card
        title="Requirements Example"
        headStyle={{ textAlign: "left" }}
        style={{ marginTop: 16 }}
      >
        <Table
          rowKey={(r, idx) => `${r.requirement}-${idx}`}
          dataSource={requirementsExamples}
          pagination={false}
          size="small"
          scroll={{ x: true }}
          columns={[
            {
              title: "Requirements Example",
              dataIndex: "requirement",
              key: "requirement",
            },
            {
              title: "Detail",
              dataIndex: "detail",
              key: "detail",
              render: (v) => v || "-",
            },
            {
              title: "Standard Quote",
              dataIndex: "standardQuote",
              key: "standardQuote",
              render: (v) => v || "-",
            },
            {
              title: "WC",
              dataIndex: "wc",
              key: "wc",
              render: (v) => v || "-",
            },
          ]}
        />
      </Card> */}
    </div>
  );
};

export default QuotationPricing;
